import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  adminListGiveaways, adminCreateGiveaway, adminUpdateGiveaway, adminDeleteGiveaway,
  adminGiveawayEntries, adminPickWinner, adminClearWinner, adminDeleteEntry,
  adminUploadGiveawayCover,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import {
  Gift, Plus, Pencil, Trash2, Trophy, Users, Calendar, Copy, Check,
  ExternalLink, RefreshCcw, X, Save, Sparkles, ArrowLeft, ShieldAlert,
  Upload, Image as ImageIcon, Loader2,
} from 'lucide-react';

const PUBLIC_BASE = (() => {
  try {
    const o = (typeof window !== 'undefined') ? window.location.origin : '';
    return (o || '').replace(/\/+$/, '');
  } catch { return ''; }
})();

const STATUS_META = {
  draft: { label: 'Draft', cls: 'bg-white/10 text-muted-foreground border border-white/10' },
  coming_soon: { label: 'Coming Soon', cls: 'bg-[rgba(108,176,255,0.10)] text-[hsl(212_80%_70%)] border border-[rgba(108,176,255,0.32)]' },
  active: { label: 'Live', cls: 'bg-[rgba(152,228,113,0.10)] text-[hsl(152_78%_55%)] border border-[rgba(152,228,113,0.32)]' },
  ended: { label: 'Ended', cls: 'bg-[rgba(225,29,46,0.10)] text-[hsl(0_72%_60%)] border border-[rgba(225,29,46,0.32)]' },
  winner_picked: { label: 'Winner picked', cls: 'bg-[rgba(245,185,66,0.10)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.32)]' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso.slice(0, 16).replace('T', ' '); }
}

function isoToLocalInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

function localInputToIso(local) {
  if (!local) return '';
  try { return new Date(local).toISOString(); } catch { return ''; }
}

function CopyBtn({ text, label = 'Copy' }) {
  const [done, setDone] = useState(false);
  return (
    <Button type="button" variant="outline" size="sm"
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }
        catch { toast.error('Copy failed'); }
      }}
      className="h-7 px-2 gap-1 border-white/10 bg-white/[0.02] text-xs">
      {done ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {done ? 'Copied' : label}
    </Button>
  );
}

const EMPTY_FORM = {
  title: '',
  description: '',
  prize_label: '',
  prize_value_usd: '',
  cover_image_url: '',
  starts_at: '',
  ends_at: '',
  status: 'active',
  show_entry_count: true,
  require_email: true,
  require_telegram: false,
  require_pocket_option_id: false,
  require_quotex_id: false,
  require_whatsapp: false,
  require_facebook: false,
  min_deposit_usd: '',
  custom_fields: [],
};

function GiveawayForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(() => {
    if (!initial) {
      // Default: starts now, ends in 7 days
      const now = new Date();
      const inWeek = new Date(Date.now() + 7 * 86400000);
      return {
        ...EMPTY_FORM,
        starts_at: isoToLocalInput(now.toISOString()),
        ends_at: isoToLocalInput(inWeek.toISOString()),
      };
    }
    return {
      title: initial.title || '',
      description: initial.description || '',
      prize_label: initial.prize_label || '',
      prize_value_usd: initial.prize_value_usd ?? '',
      cover_image_url: initial.cover_image_url || '',
      starts_at: isoToLocalInput(initial.starts_at),
      ends_at: isoToLocalInput(initial.ends_at),
      status: initial.status || 'active',
      show_entry_count: initial.show_entry_count !== false,
      require_email: initial.require_email !== false,
      require_telegram: !!initial.require_telegram,
      require_pocket_option_id: !!initial.require_pocket_option_id,
      require_quotex_id: !!initial.require_quotex_id,
      require_whatsapp: !!initial.require_whatsapp,
      require_facebook: !!initial.require_facebook,
      min_deposit_usd: initial.min_deposit_usd ?? '',
      custom_fields: Array.isArray(initial.custom_fields) ? initial.custom_fields : [],
    };
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  function up(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  async function handleUpload(file) {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Max 8 MB'); return; }
    setUploading(true);
    try {
      const res = await adminUploadGiveawayCover(file);
      if (res?.url) {
        up('cover_image_url', res.url);
        toast.success('Banner uploaded');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  }

  function addField() {
    setForm((s) => ({
      ...s,
      custom_fields: [...s.custom_fields, { key: `field_${s.custom_fields.length + 1}`, label: 'New question', required: false, type: 'text' }],
    }));
  }
  function removeField(i) {
    setForm((s) => ({ ...s, custom_fields: s.custom_fields.filter((_, idx) => idx !== i) }));
  }
  function upField(i, k, v) {
    setForm((s) => ({
      ...s,
      custom_fields: s.custom_fields.map((cf, idx) => (idx === i ? { ...cf, [k]: v } : cf)),
    }));
  }

  async function submit(e) {
    e.preventDefault();
    if (saving) return;
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!form.starts_at || !form.ends_at) { toast.error('Start + end dates required'); return; }
    const startsIso = localInputToIso(form.starts_at);
    const endsIso = localInputToIso(form.ends_at);
    if (new Date(endsIso) <= new Date(startsIso)) { toast.error('End must be after start'); return; }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      prize_label: form.prize_label.trim(),
      prize_value_usd: form.prize_value_usd === '' ? null : Number(form.prize_value_usd),
      cover_image_url: form.cover_image_url.trim(),
      starts_at: startsIso,
      ends_at: endsIso,
      status: form.status,
      show_entry_count: !!form.show_entry_count,
      require_email: !!form.require_email,
      require_telegram: !!form.require_telegram,
      require_pocket_option_id: !!form.require_pocket_option_id,
      require_quotex_id: !!form.require_quotex_id,
      require_whatsapp: !!form.require_whatsapp,
      require_facebook: !!form.require_facebook,
      min_deposit_usd: form.min_deposit_usd === '' || form.min_deposit_usd == null
        ? 0
        : (Number(form.min_deposit_usd) || 0),
      custom_fields: (form.custom_fields || []).map((cf) => ({
        key: (cf.key || '').trim() || `f_${Math.random().toString(36).slice(2, 7)}`,
        label: (cf.label || '').trim() || 'Question',
        required: !!cf.required,
        type: ['text', 'email', 'url', 'tel'].includes(cf.type) ? cf.type : 'text',
      })),
    };

    setSaving(true);
    try {
      if (initial?.id) {
        await adminUpdateGiveaway(initial.id, payload);
        toast.success('Giveaway updated');
      } else {
        await adminCreateGiveaway(payload);
        toast.success('Giveaway created');
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="giveaway-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Title *</Label>
          <Input required value={form.title} onChange={(e) => up('title', e.target.value)} maxLength={120}
            placeholder="$100 Pocket Option Account Giveaway"
            className="mt-1 bg-white/[0.02] border-white/10" data-testid="gv-form-title" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Description</Label>
          <Textarea value={form.description} onChange={(e) => up('description', e.target.value)} maxLength={4000}
            rows={4} placeholder="What you'll win, eligibility, how the winner gets paid…"
            className="mt-1 bg-white/[0.02] border-white/10" data-testid="gv-form-description" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Prize Label</Label>
          <Input value={form.prize_label} onChange={(e) => up('prize_label', e.target.value)} maxLength={60}
            placeholder="$100 Trading Account"
            className="mt-1 bg-white/[0.02] border-white/10" data-testid="gv-form-prize-label" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Prize Value (USD)</Label>
          <Input type="number" step="0.01" value={form.prize_value_usd}
            onChange={(e) => up('prize_value_usd', e.target.value)}
            placeholder="100"
            className="mt-1 bg-white/[0.02] border-white/10" data-testid="gv-form-prize-value" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Banner / Cover Image</Label>
          <div className="mt-1 flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <label className={`inline-flex items-center gap-2 px-3 h-10 rounded-md border border-[rgba(245,185,66,0.32)] bg-[rgba(245,185,66,0.06)] text-[hsl(43_92%_56%)] text-xs cursor-pointer hover:bg-[rgba(245,185,66,0.12)] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                <span>{uploading ? 'Uploading…' : 'Upload banner image'}</span>
                <input type="file" accept="image/*" className="hidden"
                  data-testid="gv-form-cover-upload"
                  onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
              {form.cover_image_url && (
                <Button type="button" variant="outline" size="sm" onClick={() => up('cover_image_url', '')}
                  className="h-10 gap-1 border-white/10 bg-white/[0.02] text-xs">
                  <X className="h-3.5 w-3.5" /> Remove
                </Button>
              )}
              <span className="text-[10px] text-muted-foreground">JPG / PNG / WebP, max 8 MB</span>
            </div>
            <Input value={form.cover_image_url} onChange={(e) => up('cover_image_url', e.target.value)} maxLength={500}
              placeholder="…or paste an image URL"
              className="bg-white/[0.02] border-white/10" data-testid="gv-form-cover" />
            {form.cover_image_url && (
              <div className="rounded-md border border-white/10 overflow-hidden bg-black/30 max-w-md">
                <img src={form.cover_image_url} alt="cover preview"
                  className="w-full max-h-48 object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              </div>
            )}
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Starts at *</Label>
          <Input required type="datetime-local" value={form.starts_at}
            onChange={(e) => up('starts_at', e.target.value)}
            className="mt-1 bg-white/[0.02] border-white/10" data-testid="gv-form-starts" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Ends at *</Label>
          <Input required type="datetime-local" value={form.ends_at}
            onChange={(e) => up('ends_at', e.target.value)}
            className="mt-1 bg-white/[0.02] border-white/10" data-testid="gv-form-ends" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Status</Label>
          <select value={form.status} onChange={(e) => up('status', e.target.value)}
            className="mt-1 w-full h-10 rounded-md bg-white/[0.02] border border-white/10 px-3 text-sm"
            data-testid="gv-form-status">
            <option value="draft">Draft (hidden)</option>
            <option value="coming_soon">Coming Soon (visible, no entries yet)</option>
            <option value="active">Active (live, accepting entries)</option>
            <option value="ended">Ended (no entries, no winner)</option>
            <option value="winner_picked">Winner picked</option>
          </select>
        </div>
      </div>

      <Separator className="bg-white/5" />

      <div className="space-y-3">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)]">Entry requirements</div>
        <p className="text-[10px] text-muted-foreground -mt-1">Toggle which contact methods participants MUST provide. Email recommended for winner notification.</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs cursor-pointer">
            <span>Email required</span>
            <Switch checked={form.require_email} onCheckedChange={(v) => up('require_email', v)} data-testid="gv-form-req-email" />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs cursor-pointer">
            <span>Telegram required</span>
            <Switch checked={form.require_telegram} onCheckedChange={(v) => up('require_telegram', v)} data-testid="gv-form-req-tg" />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs cursor-pointer">
            <span>WhatsApp required</span>
            <Switch checked={form.require_whatsapp} onCheckedChange={(v) => up('require_whatsapp', v)} data-testid="gv-form-req-wa" />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs cursor-pointer">
            <span>Facebook required</span>
            <Switch checked={form.require_facebook} onCheckedChange={(v) => up('require_facebook', v)} data-testid="gv-form-req-fb" />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2 text-xs cursor-pointer">
            <span>Show entry count</span>
            <Switch checked={form.show_entry_count} onCheckedChange={(v) => up('show_entry_count', v)} data-testid="gv-form-show-count" />
          </label>
        </div>
      </div>

      <Separator className="bg-white/5" />

      <div className="space-y-3">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)]">Broker / Trading platform</div>
        <p className="text-[10px] text-muted-foreground -mt-1">
          Choose which broker Trader ID participants must submit. Enable both if the giveaway is open to either platform.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex items-center justify-between gap-3 rounded-md border border-[rgba(245,185,66,0.18)] bg-[rgba(245,185,66,0.04)] px-3 py-2.5 text-xs cursor-pointer hover:bg-[rgba(245,185,66,0.08)] transition-colors">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-md bg-[rgba(245,185,66,0.12)] border border-[rgba(245,185,66,0.32)] flex items-center justify-center text-[10px] font-bold text-[hsl(43_92%_56%)]">PO</div>
              <div>
                <div className="font-medium">Pocket Option</div>
                <div className="text-[10px] text-muted-foreground">Require PO Trader ID</div>
              </div>
            </div>
            <Switch checked={form.require_pocket_option_id} onCheckedChange={(v) => up('require_pocket_option_id', v)} data-testid="gv-form-req-po" />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-md border border-[rgba(108,176,255,0.20)] bg-[rgba(108,176,255,0.04)] px-3 py-2.5 text-xs cursor-pointer hover:bg-[rgba(108,176,255,0.08)] transition-colors">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-md bg-[rgba(108,176,255,0.12)] border border-[rgba(108,176,255,0.32)] flex items-center justify-center text-[10px] font-bold text-[hsl(212_80%_70%)]">QX</div>
              <div>
                <div className="font-medium">Quotex</div>
                <div className="text-[10px] text-muted-foreground">Require Quotex Trader ID</div>
              </div>
            </div>
            <Switch checked={form.require_quotex_id} onCheckedChange={(v) => up('require_quotex_id', v)} data-testid="gv-form-req-quotex" />
          </label>
        </div>

        {(form.require_pocket_option_id || form.require_quotex_id) && (
          <div className="rounded-md border border-[rgba(245,185,66,0.28)] bg-[rgba(245,185,66,0.04)] p-3.5 mt-2 space-y-2">
            <div className="flex items-start gap-2">
              <div className="h-6 w-6 rounded-md bg-[hsl(43_92%_56%)] text-black font-bold flex items-center justify-center text-[10px] mt-0.5 shrink-0">$</div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-[hsl(43_92%_56%)]">Minimum deposit (USD) — eligibility gate</div>
                <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">
                  Each entry's Trader ID is verified against the broker postbacks. Only traders with a NET deposit
                  (deposits − withdrawals) of at least this amount, made between giveaway start and end, qualify.
                  Set to <strong>0</strong> to disable. Withdrawals during the giveaway period auto-cancel a participant.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Required:</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.min_deposit_usd}
                onChange={(e) => up('min_deposit_usd', e.target.value)}
                placeholder="e.g. 50"
                className="w-32 h-8 bg-white/[0.02] border-white/10 text-xs font-mono"
                data-testid="gv-form-min-deposit"
              />
              <span className="text-xs text-muted-foreground">USD</span>
              {Number(form.min_deposit_usd) > 0 && (
                <span className="text-[10px] text-[hsl(152_78%_55%)] ml-2">
                  ✓ Deposit-gated giveaway
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <Separator className="bg-white/5" />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)]">Custom questions</div>
          <Button type="button" variant="outline" size="sm" onClick={addField}
            className="h-8 gap-1 border-white/10 bg-white/[0.02] text-xs" data-testid="gv-form-add-field">
            <Plus className="h-3 w-3" /> Add question
          </Button>
        </div>
        {form.custom_fields.length === 0 ? (
          <div className="text-xs text-muted-foreground">No custom questions. Default fields collected: name, email, telegram (optional), Pocket Option ID (if enabled).</div>
        ) : (
          <div className="space-y-2">
            {form.custom_fields.map((cf, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[1fr_2fr_120px_90px_36px] rounded-md border border-white/10 bg-white/[0.02] p-2">
                <Input value={cf.key} onChange={(e) => upField(i, 'key', e.target.value)} placeholder="key"
                  maxLength={40} className="bg-white/[0.02] border-white/10 text-xs" data-testid={`gv-cf-key-${i}`} />
                <Input value={cf.label} onChange={(e) => upField(i, 'label', e.target.value)} placeholder="Question label"
                  maxLength={120} className="bg-white/[0.02] border-white/10 text-xs" data-testid={`gv-cf-label-${i}`} />
                <select value={cf.type} onChange={(e) => upField(i, 'type', e.target.value)}
                  className="h-9 rounded-md bg-white/[0.02] border border-white/10 px-2 text-xs">
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="url">URL</option>
                  <option value="tel">Phone</option>
                </select>
                <label className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                  <Switch checked={!!cf.required} onCheckedChange={(v) => upField(i, 'required', v)} /> Req
                </label>
                <Button type="button" variant="ghost" size="icon" onClick={() => removeField(i)}
                  className="h-9 w-9 text-muted-foreground hover:text-[hsl(0_72%_60%)]">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Button type="submit" disabled={saving}
          className="cta-sheen border-0 text-white font-semibold gap-2 h-10" data-testid="gv-form-save-btn">
          <Save className="h-4 w-4" /> {saving ? 'Saving…' : (initial?.id ? 'Update giveaway' : 'Create giveaway')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}
          className="h-10 border-white/10 bg-white/[0.02]">Cancel</Button>
      </div>
    </form>
  );
}

function EntriesView({ giveaway, onBack, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminGiveawayEntries(giveaway.id);
      setEntries(res?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load entries');
    } finally { setLoading(false); }
  }, [giveaway.id]);

  useEffect(() => { load(); }, [load]);

  async function pickWinner() {
    if (picking) return;
    if (!window.confirm('Pick a random winner now? This sets the giveaway to "winner_picked".')) return;
    setPicking(true);
    try {
      await adminPickWinner(giveaway.id);
      toast.success('Winner picked');
      onChanged?.();
      onBack?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Pick winner failed');
    } finally { setPicking(false); }
  }

  async function clearWinner() {
    if (!window.confirm('Clear the current winner and set giveaway back to active?')) return;
    try {
      await adminClearWinner(giveaway.id);
      toast.success('Winner cleared');
      onChanged?.();
      onBack?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Clear failed');
    }
  }

  async function removeEntry(entryId) {
    if (!window.confirm('Delete this entry permanently?')) return;
    try {
      await adminDeleteEntry(giveaway.id, entryId);
      setEntries((es) => es.filter((e) => e.id !== entryId));
      toast.success('Entry deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  }

  function exportCsv() {
    if (!entries.length) { toast.error('Nothing to export'); return; }
    const cols = ['name', 'eligibility_status', 'email', 'telegram_username', 'pocket_option_id', 'quotex_id', 'whatsapp', 'facebook', 'country_code', 'ip', 'verified_deposit_usd', 'verified_withdraw_usd', 'net_deposit_usd', 'is_proxy', 'is_hosting', 'created_at'];
    const customKeys = (giveaway.custom_fields || []).map((cf) => cf.key);
    const allCols = [...cols, ...customKeys.map((k) => `cf_${k}`)];
    const rows = [allCols.join(',')].concat(entries.map((e) => {
      const base = cols.map((c) => JSON.stringify(e[c] ?? ''));
      const cust = customKeys.map((k) => JSON.stringify((e.custom_data || {})[k] ?? ''));
      return base.concat(cust).join(',');
    }));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `giveaway-${giveaway.slug}-entries.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4" data-testid="giveaway-entries-view">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Button type="button" variant="ghost" size="sm" onClick={onBack}
            className="text-xs text-muted-foreground hover:text-white gap-1 -ml-2 mb-1">
            <ArrowLeft className="h-3.5 w-3.5" /> All giveaways
          </Button>
          <h2 className="font-display uppercase text-xl">{giveaway.title}</h2>
          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-1">
            <Badge className={STATUS_META[giveaway.status]?.cls || ''}>{STATUS_META[giveaway.status]?.label || giveaway.status}</Badge>
            <span>·</span>
            <span><Users className="h-3 w-3 inline mr-1" />{entries.length} entries</span>
            <span>·</span>
            <span>Ends {fmtDate(giveaway.ends_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={load}
            className="h-9 gap-1 border-white/10 bg-white/[0.02]" data-testid="entries-refresh-btn">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={exportCsv}
            className="h-9 gap-1 border-white/10 bg-white/[0.02]" data-testid="entries-export-btn">
            Export CSV
          </Button>
          {giveaway.status === 'winner_picked' ? (
            <Button type="button" variant="outline" size="sm" onClick={clearWinner}
              className="h-9 gap-1 border-[rgba(225,29,46,0.4)] bg-[rgba(225,29,46,0.08)] text-[hsl(0_72%_60%)]"
              data-testid="clear-winner-btn">
              <X className="h-3.5 w-3.5" /> Clear winner
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={pickWinner} disabled={picking || entries.length === 0}
              className="h-9 gap-1 bg-[hsl(43_92%_56%)] text-black hover:bg-[hsl(43_92%_50%)] font-semibold"
              data-testid="pick-winner-btn">
              <Trophy className="h-3.5 w-3.5" /> {picking ? 'Picking…' : 'Pick winner'}
            </Button>
          )}
        </div>
      </div>

      {giveaway.winner && (
        <Card className="card-surface gold-hairline">
          <CardContent className="p-4 flex items-center gap-3">
            <Trophy className="h-5 w-5 text-[hsl(43_92%_56%)]" />
            <div className="text-sm">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)]">Winner</div>
              <div><strong>{giveaway.winner.name}</strong> · {giveaway.winner.email_masked} {giveaway.winner.country_code && `· ${giveaway.winner.country_code}`}</div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="card-surface">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              <Users className="h-8 w-8 opacity-30 mx-auto mb-2" /> No entries yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-white/[0.03] text-muted-foreground uppercase tracking-[0.18em] text-[10px]">
                  <tr>
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Telegram</th>
                    <th className="text-left p-3">PO ID</th>
                    <th className="text-left p-3">Quotex ID</th>
                    <th className="text-left p-3">WhatsApp</th>
                    <th className="text-left p-3">Facebook</th>
                    <th className="text-left p-3">Country</th>
                    <th className="text-left p-3">Net Dep</th>
                    <th className="text-left p-3">Flags</th>
                    <th className="text-left p-3">When</th>
                    <th className="text-right p-3">—</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const isWin = e.id === giveaway.winner_entry_id;
                    const elig = (e.eligibility_status || 'exempt').toLowerCase();
                    const eligBadgeCls = elig === 'eligible'
                      ? 'bg-[rgba(34,199,132,0.12)] text-[hsl(152_78%_55%)] border-[rgba(34,199,132,0.4)]'
                      : elig === 'pending'
                        ? 'bg-[rgba(245,185,66,0.12)] text-[hsl(43_92%_56%)] border-[rgba(245,185,66,0.4)]'
                        : elig === 'no_account'
                          ? 'bg-[rgba(225,29,46,0.12)] text-[hsl(0_72%_60%)] border-[rgba(225,29,46,0.4)]'
                          : elig === 'cancelled'
                            ? 'bg-[rgba(225,29,46,0.18)] text-[hsl(0_72%_60%)] border-[rgba(225,29,46,0.5)]'
                            : 'bg-white/[0.05] text-muted-foreground border-white/10';
                    const eligLabel = elig === 'eligible' ? 'Eligible'
                      : elig === 'pending' ? 'Pending'
                      : elig === 'no_account' ? 'No Account'
                      : elig === 'cancelled' ? 'Cancelled'
                      : '—';
                    return (
                      <tr key={e.id} className={`border-t border-white/5 ${isWin ? 'bg-[rgba(245,185,66,0.05)]' : ''} ${elig === 'cancelled' ? 'opacity-60' : ''}`}>
                        <td className="p-3">
                          {isWin && <Trophy className="h-3.5 w-3.5 text-[hsl(43_92%_56%)] inline mr-1" />}
                          {e.name}
                        </td>
                        <td className="p-3">
                          <span className={'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border uppercase tracking-[0.12em] ' + eligBadgeCls}
                            title={e.eligibility_message || ''}>
                            {eligLabel}
                          </span>
                        </td>
                        <td className="p-3 text-muted-foreground">{e.email || '—'}</td>
                        <td className="p-3 text-muted-foreground">{e.telegram_username ? `@${e.telegram_username}` : '—'}</td>
                        <td className="p-3 font-mono text-[11px] text-muted-foreground">{e.pocket_option_id || '—'}</td>
                        <td className="p-3 font-mono text-[11px] text-muted-foreground">{e.quotex_id || '—'}</td>
                        <td className="p-3 font-mono text-[11px] text-muted-foreground">{e.whatsapp || '—'}</td>
                        <td className="p-3 text-muted-foreground truncate max-w-[160px]">{e.facebook || '—'}</td>
                        <td className="p-3 text-muted-foreground">{e.country_code || '—'}</td>
                        <td className="p-3 font-mono text-[11px] text-[hsl(43_92%_56%)]">
                          {(e.net_deposit_usd != null) ? `$${Number(e.net_deposit_usd || 0).toFixed(2)}` : '—'}
                        </td>
                        <td className="p-3">
                          {e.is_proxy && <Badge className="bg-[rgba(225,29,46,0.10)] text-[hsl(0_72%_60%)] border border-[rgba(225,29,46,0.3)] text-[10px] mr-1"><ShieldAlert className="h-3 w-3 mr-0.5" /> VPN</Badge>}
                          {e.is_hosting && <Badge className="bg-[rgba(245,185,66,0.10)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.28)] text-[10px]">DC</Badge>}
                          {!e.is_proxy && !e.is_hosting && <span className="text-muted-foreground/60">—</span>}
                        </td>
                        <td className="p-3 text-muted-foreground">{fmtDate(e.created_at)}</td>
                        <td className="p-3 text-right">
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeEntry(e.id)}
                            className="h-7 w-7 text-muted-foreground hover:text-[hsl(0_72%_60%)]"
                            data-testid={`entry-delete-${e.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminGiveawaysTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ kind: 'list' }); // list | new | edit | entries

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListGiveaways();
      setItems(res?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load giveaways');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function remove(g) {
    if (!window.confirm(`Delete "${g.title}" and ALL its entries? This cannot be undone.`)) return;
    try {
      await adminDeleteGiveaway(g.id);
      toast.success('Giveaway deleted');
      setItems((xs) => xs.filter((x) => x.id !== g.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  }

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter((g) => g.status === 'active').length;
    const totalEntries = items.reduce((sum, g) => sum + (g.entries_count || 0), 0);
    const winnersPicked = items.filter((g) => g.status === 'winner_picked').length;
    return { total, active, totalEntries, winnersPicked };
  }, [items]);

  if (view.kind === 'new' || view.kind === 'edit') {
    return (
      <div className="space-y-5">
        <div>
          <Button type="button" variant="ghost" size="sm"
            onClick={() => setView({ kind: 'list' })}
            className="text-xs text-muted-foreground hover:text-white gap-1 -ml-2 mb-1">
            <ArrowLeft className="h-3.5 w-3.5" /> All giveaways
          </Button>
          <h2 className="font-display uppercase text-xl">
            {view.kind === 'new' ? 'New giveaway' : 'Edit giveaway'}
          </h2>
        </div>
        <Card className="card-surface">
          <CardContent className="p-5">
            <GiveawayForm
              initial={view.kind === 'edit' ? view.gv : null}
              onCancel={() => setView({ kind: 'list' })}
              onSaved={() => { setView({ kind: 'list' }); load(); }}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (view.kind === 'entries') {
    return (
      <EntriesView
        giveaway={view.gv}
        onBack={() => setView({ kind: 'list' })}
        onChanged={load}
      />
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-giveaways-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display uppercase text-xl flex items-center gap-2">
            <Gift className="h-5 w-5 text-[hsl(43_92%_56%)]" /> Giveaways
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Run multiple concurrent giveaways. Winners are picked automatically when the timer ends.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={load}
            className="h-9 gap-1 border-white/10 bg-white/[0.02]" data-testid="gv-refresh-btn">
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button type="button" size="sm" onClick={() => setView({ kind: 'new' })}
            className="cta-sheen border-0 text-white font-semibold gap-1 h-9"
            data-testid="gv-new-btn">
            <Plus className="h-3.5 w-3.5" /> New giveaway
          </Button>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        <Card className="card-surface"><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total</div>
          <div className="text-2xl font-semibold mt-1">{stats.total}</div>
        </CardContent></Card>
        <Card className="card-surface"><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Live</div>
          <div className="text-2xl font-semibold mt-1 text-[hsl(152_78%_55%)]">{stats.active}</div>
        </CardContent></Card>
        <Card className="card-surface"><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Entries</div>
          <div className="text-2xl font-semibold mt-1">{stats.totalEntries.toLocaleString()}</div>
        </CardContent></Card>
        <Card className="card-surface"><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Winners</div>
          <div className="text-2xl font-semibold mt-1 text-[hsl(43_92%_56%)]">{stats.winnersPicked}</div>
        </CardContent></Card>
      </div>

      <Card className="card-surface">
        <CardHeader className="border-b border-white/5">
          <CardTitle className="text-sm font-display uppercase tracking-wide flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[hsl(43_92%_56%)]" /> All giveaways
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              <Gift className="h-8 w-8 opacity-30 mx-auto mb-3" />
              <div>No giveaways yet.</div>
              <Button type="button" size="sm" onClick={() => setView({ kind: 'new' })}
                className="mt-4 cta-sheen border-0 text-white gap-1 h-9">
                <Plus className="h-3.5 w-3.5" /> Create your first giveaway
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {items.map((g) => {
                const meta = STATUS_META[g.status] || STATUS_META.active;
                const publicUrl = `${PUBLIC_BASE}/giveaways/${g.slug}`;
                return (
                  <div key={g.id} className="p-4 grid gap-3 sm:grid-cols-[1fr_auto] items-start hover:bg-white/[0.02] transition-colors">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={meta.cls + ' text-[10px]'}>{meta.label}</Badge>
                        {g.prize_label && (
                          <Badge className="bg-[hsl(43_92%_56%)] text-black text-[10px] font-bold uppercase tracking-[0.18em]">
                            {g.prize_label}
                          </Badge>
                        )}
                      </div>
                      <div className="font-semibold mt-1.5 truncate">{g.title}</div>
                      <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                        <span><Calendar className="h-3 w-3 inline mr-1" /> Ends {fmtDate(g.ends_at)}</span>
                        <span><Users className="h-3 w-3 inline mr-1" /> {(g.entries_count || 0).toLocaleString()} entries</span>
                        {g.winner && <span className="text-[hsl(43_92%_56%)]"><Trophy className="h-3 w-3 inline mr-1" />{g.winner.name}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <code className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/10 text-muted-foreground">
                          /giveaways/{g.slug}
                        </code>
                        <CopyBtn text={publicUrl} label="Copy link" />
                        <a href={publicUrl} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-[hsl(43_92%_56%)] hover:underline">
                          <ExternalLink className="h-3 w-3" /> View
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <Button type="button" variant="outline" size="sm"
                        onClick={() => setView({ kind: 'entries', gv: g })}
                        className="h-8 gap-1 border-white/10 bg-white/[0.02] text-xs"
                        data-testid={`gv-row-entries-${g.slug}`}>
                        <Users className="h-3.5 w-3.5" /> Entries
                      </Button>
                      <Button type="button" variant="outline" size="sm"
                        onClick={() => setView({ kind: 'edit', gv: g })}
                        className="h-8 gap-1 border-white/10 bg-white/[0.02] text-xs"
                        data-testid={`gv-row-edit-${g.slug}`}>
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </Button>
                      <Button type="button" variant="ghost" size="icon"
                        onClick={() => remove(g)}
                        className="h-8 w-8 text-muted-foreground hover:text-[hsl(0_72%_60%)]"
                        data-testid={`gv-row-delete-${g.slug}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
